import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { customerApiClient } from "@/lib/auth";
import { customerKeys } from "@/lib/query-keys";

// The logo read may be cached for an hour, so the query holds the blob as long.
// The QUERY caches the Blob; the object URL is per hook instance, minted and
// revoked in an effect keyed on the blob, so a cached Blob mints a fresh URL on
// remount instead of handing out a revoked one (the use-customer-attachments
// pattern).
const LOGO_STALE_TIME = 60 * 60 * 1000;

/**
 * The utility's public logo as an object URL, or null: without a slug, when
 * the utility has no logo (no request is made), while the read is in flight,
 * and on any refusal, a 404 included. The URL is revoked when the logo changes
 * and on unmount.
 */
export function usePortalLogo(slug: string | null, hasLogo: boolean): string | null {
  const portalSlug = slug ?? "";
  const enabled = portalSlug !== "" && hasLogo;
  const query = useQuery<Blob>({
    queryKey: customerKeys.portalLogo(portalSlug),
    queryFn: async () => {
      const { data, error, response } = await customerApiClient.GET("/portal/{slug}/logo", {
        params: { path: { slug: portalSlug } },
        parseAs: "blob",
      });
      if (error) {
        throw Object.assign(new Error("portal logo fetch failed"), { status: response.status });
      }
      // parseAs:"blob" returns a runtime Blob; the generated type for the
      // octet-stream body is string, so the cast realigns the static type with
      // the parse mode (the use-customer-attachments shape).
      return data as unknown as Blob;
    },
    enabled,
    staleTime: LOGO_STALE_TIME,
    retry: false,
  });

  // An errored refetch keeps the previous blob in the cache; the logo shown is
  // only ever the one the latest read answered.
  const blob = enabled && !query.isError ? (query.data ?? null) : null;
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!blob) {
      setObjectUrl(null);
      return;
    }
    const url = URL.createObjectURL(blob);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [blob]);

  return blob ? objectUrl : null;
}
